import { AddOnsProduct, AddonType, BookingSystem } from 'app/shared/model';
import React, { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { UpdateDraftAddOnProduct, listAddOnsProductsData, loadDraftAddOnProduct } from '../actions';
import { getAccountCategories, getAddonById, getDraftAddOnProduct, getFormErrors, getProductCategories, getUnitTypes } from '../selectors';
import * as api from 'app/shared/api';
import { DivisionId } from 'app/shared/division/getDivisionId';
import { Form, Input, Select, Button, message, InputNumber, Row, Col, Checkbox, Switch, Alert, Tooltip } from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import { ApiErrors } from 'app/shared/api/ApiErrors';
import { PRODUCT_ACTIVE_PACKAGE_TOOLTIP } from 'app/shared/api/productDtoHelpers';
import { useActivePackageDeactivationLock } from '../hooks/useActivePackageDeactivationLock';

interface AddonFormProps {
  onSuccess?: () => void;
}

const ADDON_PRODUCT_TYPE_ID = 3;

function AddEditAddonForm({ onSuccess }: AddonFormProps) {
    const dispatch = useDispatch<any>();
    const addonProduct = useSelector(getAddonById)!;
    const unitTypes = useSelector(getUnitTypes);
    const productCategories = useSelector(getProductCategories) || [];
    const accountCategories = useSelector(getAccountCategories) || [];
    const draftProduct = useSelector(getDraftAddOnProduct);
    const formErrors = useSelector(getFormErrors);
    const [form] = Form.useForm();
    const watchedId = Form.useWatch('id', form);
    const watchedTypeId = Form.useWatch('typeId', form);
    const [loading, setLoading] = React.useState(false);
    const [productUpdated, setProductUpdated] = React.useState<string>("");
    const [isError, setIsError] = React.useState(false);
    const [bookingSystems, setBookingSystems] = React.useState<BookingSystem[]>([]);
    const [addonTypes, setAddonTypes] = React.useState<AddonType[]>([]);
    const [isExclusionLoading, setIsExclusionLoading] = React.useState(false);
    const [initialExcludedSystems, setInitialExcludedSystems] = React.useState<string[]>([]);
    const current: Partial<AddOnsProduct> = {
      id: draftProduct?.id ?? addonProduct?.id ?? 0,
      name: draftProduct?.name ?? addonProduct?.name ?? '',
      unitTypeId: (typeof (draftProduct?.unitTypeId ?? addonProduct?.unitTypeId) === 'string')
        ? Number(draftProduct?.unitTypeId ?? addonProduct?.unitTypeId)
        : (draftProduct?.unitTypeId ?? addonProduct?.unitTypeId),
      productCategoryId: draftProduct?.productCategoryId ?? addonProduct?.productCategoryId,
      accountCategoryId: draftProduct?.accountCategoryId ?? addonProduct?.accountCategoryId,
      numberOfOneToOneLessonsPerWeek: draftProduct?.numberOfOneToOneLessonsPerWeek ?? addonProduct?.numberOfOneToOneLessonsPerWeek,
      typeId: draftProduct?.typeId ?? addonProduct?.typeId,
        minimumAge: draftProduct?.minimumAge ?? addonProduct?.minimumAge ?? null,
        excludedBookingSystemIds: draftProduct?.excludedBookingSystemIds ?? addonProduct?.excludedBookingSystemIds ?? [],
      active: draftProduct?.active ?? addonProduct?.active ?? true,
    };
    const nz = (v: any) => {
      const n = v === '' ? undefined : Number(v);
      return n && n > 0 ? n : undefined;
    };

    useEffect(() => {
      let isMounted = true;
      api.getAllBookingSystems()
        .then(systems => {
          if (isMounted) {
            setBookingSystems(systems);
          }
        })
        .catch((err: any) => {
          message.error(err?.message ?? 'Failed to load booking systems');
        });
      return () => {
        isMounted = false;
      };
    }, []);

    useEffect(() => {
      let isMounted = true;
      api.getAllAddonTypes()
        .then(types => {
          if (isMounted) {
            setAddonTypes(types);
          }
        })
        .catch((err: any) => {
          message.error(err?.message ?? 'Failed to load addon types');
        });
      return () => {
        isMounted = false;
      };
    }, []);

    useEffect(() => {
      dispatch(loadDraftAddOnProduct(undefined!));
      form.setFieldsValue({
        name: addonProduct?.name || '',
        unitTypeId: nz(addonProduct?.unitTypeId),
        productCategoryId: nz(addonProduct?.productCategoryId),
        accountCategoryId: nz(addonProduct?.accountCategoryId),
        numberOfOneToOneLessonsPerWeek: addonProduct?.numberOfOneToOneLessonsPerWeek,
        
        id: addonProduct?.id ?? 0,
        typeId: nz(addonProduct?.typeId),
          minimumAge: nz(addonProduct?.minimumAge),
        excludedBookingSystemIds: addonProduct?.excludedBookingSystemIds ?? [],
        active: addonProduct?.active !== false,
      });

    }, [addonProduct]);

    useEffect(() => {
      const productId = addonProduct?.id ?? 0;
      if (!productId) {
        setInitialExcludedSystems([]);
        form.setFieldsValue({ excludedBookingSystemIds: [] });
        setIsExclusionLoading(false);
        return;
      }

      let cancelled = false;
      setIsExclusionLoading(true);
      api.getExcludedBookingSystemIdsForProduct(productId, ADDON_PRODUCT_TYPE_ID)
        .then(ids => {
          if (!cancelled) {
            setInitialExcludedSystems(ids);
          }
        })
        .catch((err: any) => {
          if (!cancelled) {
            setInitialExcludedSystems([]);
            message.error(err?.message ?? 'Failed to load booking system exclusions');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsExclusionLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [addonProduct?.id, form]);

    useEffect(() => {
      form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
    }, [initialExcludedSystems, form]);

    useEffect(() => {
        if (formErrors && formErrors.length > 0) {
            formErrors.forEach((err) =>
                form.setFields([{ name: err.name, errors: [err.error] }])
            );
        }
    }, [formErrors, form]);

    const toNum = (v: any) => (v === undefined || v === null ? NaN : Number(v));
        const pickId = (o: any) => toNum(o?.id ?? o?.courseIntensityId ?? o?.categoryId ?? o?.value);
        const pickName = (o: any) => (o?.name ?? o?.text ?? o?.title ?? o?.label ?? "");
    
        const nameById = React.useMemo(() => ({
          unitType: new Map(unitTypes.map(i => [pickId(i), pickName(i)])),
          product:   new Map(productCategories.map(c => [pickId(c), pickName(c)])),
          account:   new Map(accountCategories.map(c => [pickId(c), pickName(c)])),
        }), [
          unitTypes,
          productCategories,
          accountCategories,
        ]);

  const handleValuesChange = (changed: any, all: any) => {
    const updated: AddOnsProduct & { oneToOneLessonsPerWeek?: number } = {
      ...(draftProduct || addonProduct || {} as AddOnsProduct),
      ...all,
    };
    if (Array.isArray(all?.excludedBookingSystemIds)) {
      updated.excludedBookingSystemIds = all.excludedBookingSystemIds.map((id: string | number) => String(id));
    } else if (!all?.excludedBookingSystemIds) {
      updated.excludedBookingSystemIds = [];
    }
    dispatch(UpdateDraftAddOnProduct(updated));
  };

const extractApiErrors = (payload: any): string[] => {
  if (payload?.errors && Array.isArray(payload.errors) && payload.errors.length > 0) {
    return payload.errors;
  }
  
  return ApiErrors.extractAllErrors(payload, undefined, 'Unknown error');
};

    const formDisabled = Boolean(addonProduct?.entityUpdate);
    const unitTypeLocked =
      !formDisabled &&
      Number(addonProduct?.id ?? 0) > 0 &&
      addonProduct?.hasActiveOffering === true;
    const { activeDeactivationLocked } = useActivePackageDeactivationLock(
      addonProduct ?? draftProduct,
      ADDON_PRODUCT_TYPE_ID,
      DivisionId(),
      addonProduct ?? draftProduct
    );

    const handleSubmit = async () => {
        try {
          const values = await form.validateFields();
            setLoading(true);
            const originalId = values.id ?? addonProduct?.id ?? draftProduct?.id ?? 0;
            const unitTypeIdResolved = unitTypeLocked
              ? Number(addonProduct?.unitTypeId ?? draftProduct?.unitTypeId ?? 0)
              : Number(values.unitTypeId);
            const draft: AddOnsProduct & { numberOfOneToOneLessonsPerWeek?: number } = {
                ...(draftProduct || addonProduct || {} as AddOnsProduct),
                ...values,
                id: values.id ?? 0,
                unitTypeId: unitTypeIdResolved,
                active: activeDeactivationLocked ? true : values.active !== false,
            };
            const result = await api.saveAddOnProduct(draft, DivisionId());

            if (!result?.ok) {
                const errs = extractApiErrors(result);
                setIsError(true);
                if (errs.length > 0) {
                    message.error(errs.join('; '));
                } else {
                    message.error('Failed to save add-on product');
                }
                return;
            }

            const savedProductIdRaw = result.productId ?? draft.id ?? values.id ?? 0;
            const savedProductId = Number(savedProductIdRaw);
            const selectedSystems = form.getFieldValue('excludedBookingSystemIds') as (string | number)[] | undefined;

            if (savedProductId > 0) {
                try {
                    const updatedIds = await api.updateBookingSystemProductExclusions(
                        savedProductId,
                        ADDON_PRODUCT_TYPE_ID,
                        selectedSystems,
                        initialExcludedSystems
                    );
                    setInitialExcludedSystems(updatedIds);
                } catch (err: any) {
                    setIsError(true);
                    message.error(err?.message ?? 'Failed to update booking system exclusions');
                    return;
                }
            } else if (Array.isArray(selectedSystems) && selectedSystems.length > 0) {
                message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
            }

            const safeList:AddOnsProduct[] = await api.getAddonsProducts(DivisionId()) || [];
            const itemIndex = safeList.findIndex(a => a.id === savedProductId);
            
            if(result?.productId && itemIndex === -1)
            {
              draft.id = savedProductId || 0;
              draft.active = true;
              draft.accountCategoryText = nameById.account.get(Number(values.accountCategoryId)) ?? "";
              draft.productCategoryText = nameById.product.get(Number(values.productCategoryId)) ?? "";
              draft.unitTypeText = nameById.unitType.get(Number(unitTypeIdResolved)) ?? "";
              draft.entityUpdate = true;
              
              safeList.push(draft);

              dispatch(listAddOnsProductsData(safeList));
            }
            else
            {
                safeList[itemIndex] = { ...safeList[itemIndex], entityUpdate: true };
                dispatch(listAddOnsProductsData(safeList));
            }

            const successText = originalId && originalId > 0 ? "Add-on product updated!" : "Add-on product created!";
            setIsError(false);
            message.success(successText);
            onSuccess?.();
        } catch (error: any) {
            if (error?.errorFields) {
                setIsError(true);
                return;
            }
            const errs = extractApiErrors(error);
            setIsError(true);
            if (errs.length > 0) {
                message.error(errs.join('; '));
            } else {
                message.error('Failed to save add-on product');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            layout="vertical"
            form={form}
            disabled={formDisabled}
            initialValues={{
                name: current.name ?? "",
                UnitTypeId: current.unitTypeId,
                productCategoryId: current.productCategoryId,
                accountCategoryId: current.accountCategoryId,
                id: current.id ?? 0,
                typeId: current.typeId,
                minimumAge: current.minimumAge ?? undefined,
                excludedBookingSystemIds: current.excludedBookingSystemIds ?? [],
                active: current.active !== false,
                numberOfOneToOneLessonsPerWeek: current.numberOfOneToOneLessonsPerWeek
            }}
            onValuesChange={handleValuesChange}
            onFinish={handleSubmit}
        >
  {formDisabled && (
    <Alert type="info" title="Publishing an update" description="This record is locked while an update is being published. Editing is disabled." showIcon style={{ marginBottom: 16 }} />
  )}
  <Row gutter={24}>
    <Col span={24}>
      <Form.Item
        label="Name"
        name="name"
        rules={[
          { required: true, message: 'Please enter add-on name' },
          { max: 100, message: "Max length is 100 characters" },
        ]}
      >
        <Input maxLength={100} placeholder='Enter Add-on name ' />
      </Form.Item>
    </Col>

    <Col span={0}>
      <Form.Item hidden label="ID" name="id">
        <Input disabled />
      </Form.Item>
    </Col>
  </Row>

  {watchedId === 1 && (
    <Row gutter={24}>
      <Col span={24}>
        <Form.Item
          label="Number of One to One Lessons Per Week"
          name="oneToOneLessonsPerWeek"
          rules={[{ required: true, message: 'Please enter the number of lessons' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
  )}

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Type"
        name="typeId"
        rules={[{ required: true, message: 'Please select a type' }]}
      >
        <Select
          placeholder="Select Type"
          options={addonTypes.map(type => ({
            label: type.name,
            value: Number(type.id),
            key: type.id,
          }))}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
        />
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item label="Unit Type" required>
        <Tooltip title={unitTypeLocked ? 'Has active offering' : undefined}>
          <span style={{ display: 'block', width: '100%' }}>
            <Form.Item
              name="unitTypeId"
              noStyle
              rules={[{ required: true, message: 'Please select a unit type' }]}
            >
              <Select
                placeholder="Select Unit Type"
                options={unitTypes
                  .filter(i => i.id > 0)
                  .filter(i =>
                    (i.allowedProductTypes ?? []).some(pt => 
                      String(pt?.name).toLowerCase() === 'addon' ||
                      Number(pt?.id) === ADDON_PRODUCT_TYPE_ID
                    )
                  )
                  .map(i => ({
                  label: i.name,
                  value: Number(i.id),
                  key: i.id,
                }))}
                showSearch={unitTypeLocked ? false : { optionFilterProp: 'label' }}
                allowClear={!unitTypeLocked}
                disabled={formDisabled || unitTypeLocked}
              />
            </Form.Item>
          </span>
        </Tooltip>
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Product Category"
        name="productCategoryId"
        rules={[{ required: true, message: 'Please select a product category' }]}
      >
        <Select
          placeholder="Select Product Category"
          options={productCategories.map(i => ({
            label: i.name,
            value: i.id,
            key: i.id,
          }))}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
        />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item
        label="Account Category"
        name="accountCategoryId"
        rules={[{ required: true, message: 'Please select an account category' }]}
      >
        <Select
          placeholder="Select Account Category"
          options={accountCategories.map(i => ({
            label: i.name,
            value: i.id,
            key: i.id,
          }))}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
        />
      </Form.Item>
    </Col>

      <Col span={12}>
          <Form.Item
              label="Min Age"
              name="minimumAge"
              rules={[
                  { type: 'number', min: 1, message: 'Must be at least 1' }
              ]}
          >
              <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter minimum age"
                  min={1}
              />
          </Form.Item>
      </Col>

      {Number(watchedTypeId) === 1 && (
        <Col span={12}>
          <Form.Item
              label="No. of One-to-One Lessons per Week"
              name="numberOfOneToOneLessonsPerWeek"
              rules={[
                  { required: true, message: 'Please enter no. of one-to-one lessons per week' },
                  { type: 'number', min: 1, message: 'Must be at least 1' }
              ]}
          >
              <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter no. of one-to-one lessons"
                  min={1}
              />
          </Form.Item>
      </Col>
      )}
  </Row>
  <Row gutter={16}>
    <Col span={12}>
      <ActiveStatusFormItem
        name="active"
        tooltip={activeDeactivationLocked ? PRODUCT_ACTIVE_PACKAGE_TOOLTIP : undefined}
        disabled={activeDeactivationLocked}
      />
    </Col>
  </Row>
  <Row>
    <Col span={24}>
      <Form.Item
        name="excludedBookingSystemIds"
        label={
          <>
            <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
            {' this add-on product from the following systems:'}
          </>
        }
      >
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          disabled={isExclusionLoading}
        >
          {bookingSystems.map(system => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </Col>
  </Row>

  <Form.Item style={{ textAlign: 'right' }}>
    <Button
      type="primary"
      htmlType="submit"
      loading={loading}
      disabled={formDisabled}
      style={{ minWidth: 100 }}
    >
      Save
    </Button>
  </Form.Item>
</Form>
    );
}

export default AddEditAddonForm;